import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { clsx } from 'clsx'
import { z } from 'zod'
import { ApiError } from '@/shared/api/client'
import { isValidCpf } from '@/shared/lib/cpf'
import { useUpdatePersonalData, type PersonalData } from '../queries'
import styles from './AccountForm.module.css'

const UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
] as const

function isAdult(iso: string): boolean {
  const birth = new Date(iso)
  if (Number.isNaN(birth.getTime())) return false
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
  return birth <= today && age >= 18
}

const schema = z.object({
  fullName: z.string().trim().max(120, 'Máximo 120 caracteres')
    .refine((v) => v === '' || v.length >= 2, 'Nome muito curto.'),
  cpf: z.string().trim().refine((v) => v === '' || isValidCpf(v), 'CPF inválido.'),
  birthDate: z.string().refine((v) => v === '' || isAdult(v), 'É necessário ter pelo menos 18 anos.'),
  phone: z.string().trim(),
  uf: z.string().trim(),
  city: z.string().trim().max(80, 'Máximo 80 caracteres'),
})

type FormValues = z.infer<typeof schema>

function toDefaults(data: PersonalData): FormValues {
  return {
    fullName: data.fullName ?? '',
    cpf: '',
    birthDate: data.birthDate ?? '',
    phone: data.phone ?? '',
    uf: data.uf ?? '',
    city: data.city ?? '',
  }
}

export function PersonalDataForm({ data }: { data: PersonalData }) {
  const mutation = useUpdatePersonalData()
  const {
    register, handleSubmit, reset, setError,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: toDefaults(data) })

  // Keep the form in sync after a save (server returns the new masked state).
  useEffect(() => { reset(toDefaults(data)) }, [data, reset])

  function onSubmit(values: FormValues) {
    mutation.mutate(
      {
        fullName: values.fullName || null,
        cpf: values.cpf || null,
        birthDate: values.birthDate || null,
        phone: values.phone || null,
        uf: values.uf || null,
        city: values.city || null,
      },
      {
        onError: (err) => {
          if (err instanceof ApiError && err.errorCode === 'cpf_already_used') {
            setError('cpf', { message: 'Este CPF já está em uso em outra conta.' })
          } else if (err instanceof ApiError && err.errorCode === 'validation_failed') {
            setError('root', { message: 'Verifique os campos e tente novamente.' })
          } else {
            setError('root', { message: 'Não foi possível salvar. Tente novamente.' })
          }
        },
      },
    )
  }

  return (
    <form className={clsx(styles.card)} onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className={styles.cardHead}>
        <span className={styles.cardTitle}>Dados pessoais</span>
        <span className={styles.cardHint}>🔒 Privado · usado para pagamentos e verificação</span>
      </div>

      <div className={styles.cardBody}>
        <div className={styles.grid}>
          <div className={clsx(styles.field, styles.col2)}>
            <label className={styles.label} htmlFor="fullName">Nome completo (como no documento)</label>
            <input id="fullName" className={clsx(styles.input, errors.fullName && styles.hasError)}
              placeholder="Ex.: Bruno Carvalho de Souza" {...register('fullName')} />
            {errors.fullName
              ? <span className={styles.error}>{errors.fullName.message}</span>
              : <span className={styles.help}>Deve coincidir com o titular da chave Pix para receber prêmios.</span>}
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="cpf">CPF</label>
            <input id="cpf" inputMode="numeric"
              className={clsx(styles.input, styles.mono, errors.cpf && styles.hasError)}
              placeholder={data.hasCpf ? (data.cpfMask ?? '•••.•••.•••-••') : '000.000.000-00'}
              {...register('cpf')} />
            {errors.cpf
              ? <span className={styles.error}>{errors.cpf.message}</span>
              : <span className={styles.help}>{data.hasCpf ? 'Preencha apenas para alterar o CPF atual.' : 'Necessário para ligas pagas.'}</span>}
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="birthDate">Data de nascimento</label>
            <input id="birthDate" type="date"
              className={clsx(styles.input, styles.mono, errors.birthDate && styles.hasError)}
              {...register('birthDate')} />
            {errors.birthDate && <span className={styles.error}>{errors.birthDate.message}</span>}
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="phone">Telefone (WhatsApp)</label>
            <input id="phone" inputMode="tel"
              className={clsx(styles.input, styles.mono)} placeholder="(11) 90000-0000" {...register('phone')} />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="uf">Estado (UF)</label>
            <select id="uf" className={styles.select} {...register('uf')}>
              <option value="">—</option>
              {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
            </select>
          </div>

          <div className={clsx(styles.field, styles.col2)}>
            <label className={styles.label} htmlFor="city">Cidade</label>
            <input id="city" className={styles.input} placeholder="Sua cidade" {...register('city')} />
          </div>
        </div>

        <div className={styles.actions}>
          {errors.root && <span className={styles.error}>{errors.root.message}</span>}
          {mutation.isSuccess && !isDirty && <span className={styles.savedNote}>✓ Salvo</span>}
          <button type="submit" className={styles.btnPrimary} disabled={isSubmitting || mutation.isPending}>
            {mutation.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>
    </form>
  )
}
